import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { SEOHead } from "@/components/seo/SEOHead.tsx";

const T = {
  ink: "#0a0a0a", inkSoft: "#6b6b6b", rule: "#e9e6df",
  paper: "#faf8f3", paper2: "#f3efe5", accent: "#ff5b1f",
};

/**
 * Hardcoded seed articles. In Phase 2 these will be fetched from
 * GET /cms/pages?category=blog&status=published.
 */
export const SEED_POSTS = [
  {
    slug: "what-is-a-faceless-youtube-channel",
    title: "What Is a Faceless YouTube Channel? (And How to Start One in 2026)",
    excerpt: "Faceless channels are booming. Here's what they are, why they work, and the exact steps to launch one without ever turning on a camera.",
    category: "Getting Started",
    date: "2026-04-15",
    readTime: "7 min read",
  },
  {
    slug: "best-niches-for-faceless-youtube",
    title: "12 Best Niches for Faceless YouTube Channels in 2026",
    excerpt: "History, finance, science, motivation — we break down the most profitable faceless niches with estimated CPMs and competition levels.",
    category: "Strategy",
    date: "2026-04-08",
    readTime: "9 min read",
  },
  {
    slug: "ai-video-generation-workflow",
    title: "The Complete AI Video Generation Workflow: Script → Storyboard → Publish",
    excerpt: "A step-by-step walkthrough of how AI-powered tools like TubeForge turn a topic into a publish-ready video in a few steps.",
    category: "Product",
    date: "2026-03-28",
    readTime: "6 min read",
  },
  {
    slug: "youtube-shorts-vs-long-form",
    title: "YouTube Shorts vs Long-Form: Which Should You Focus On?",
    excerpt: "Shorts drive subscribers; long-form drives revenue. We look at the data and help you decide the right mix for your faceless channel.",
    category: "Strategy",
    date: "2026-03-20",
    readTime: "5 min read",
  },
  {
    slug: "ai-voiceover-comparison",
    title: "AI Voiceover Showdown: OpenAI TTS vs ElevenLabs for YouTube",
    excerpt: "We compare the two AI voice providers available in TubeForge — quality, naturalness, language support, and when to upgrade.",
    category: "Product",
    date: "2026-03-12",
    readTime: "4 min read",
  },
  {
    slug: "how-to-write-hooks-that-keep-viewers",
    title: "How to Write Hooks That Keep Viewers Past the First 5 Seconds",
    excerpt: "The hook is everything on YouTube. Here are 7 proven hook formulas that work especially well for faceless narrated videos.",
    category: "Writing",
    date: "2026-03-05",
    readTime: "6 min read",
  },
];

const CATEGORIES = ["All", ...Array.from(new Set(SEED_POSTS.map(p => p.category)))];

export function BlogIndexPage() {
  const [activeCategory, setActiveCategory] = useState("All");
  const [email, setEmail] = useState("");
  const [subscribed, setSubscribed] = useState(false);

  const posts = activeCategory === "All"
    ? SEED_POSTS
    : SEED_POSTS.filter(p => p.category === activeCategory);

  function handleSubscribe() {
    if (!email.trim() || !email.includes("@")) return;
    // Phase 2: POST /api/newsletter { email }
    setSubscribed(true);
  }

  return (
    <>
      <SEOHead
        title="Faceless YouTube Creator Blog"
        description="Tips, strategies, and guides for faceless YouTube creators. Learn about AI video generation, niche selection, scripting, and publishing."
        path="/blog"
      />

      {/* Hero */}
      <section className="py-16 md:py-20 text-center" style={{ backgroundColor: T.paper }}>
        <div className="mx-auto max-w-3xl px-5 md:px-8">
          <p className="text-[13px] font-semibold uppercase tracking-widest mb-4" style={{ color: T.accent }}>
            Blog
          </p>
          <h1
            className="text-3xl md:text-4xl font-bold leading-tight mb-5"
            style={{ fontFamily: '"Bricolage Grotesque", "Inter", system-ui', letterSpacing: "-0.03em" }}
          >
            Faceless YouTube creator guides
          </h1>
          <p className="text-[16px] leading-relaxed max-w-lg mx-auto" style={{ color: T.inkSoft }}>
            Niche selection, scripting, AI tools, and growth strategies — everything you need to build a faceless YouTube channel.
          </p>
        </div>
      </section>

      {/* Category filter */}
      <section style={{ backgroundColor: T.paper, borderBottom: `1px solid ${T.rule}` }}>
        <div className="mx-auto max-w-6xl px-5 md:px-8">
          <div className="flex gap-2 overflow-x-auto pb-4">
            {CATEGORIES.map((cat) => {
              const isActive = cat === activeCategory;
              return (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className="text-[13px] font-medium px-4 py-2 rounded-lg whitespace-nowrap"
                  style={{
                    backgroundColor: isActive ? T.accent : "#fff",
                    color: isActive ? "#fff" : T.ink,
                    border: `1px solid ${isActive ? T.accent : T.rule}`,
                    cursor: "pointer",
                    fontFamily: "inherit",
                  }}
                >
                  {cat}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Post grid */}
      <section className="py-12 md:py-16" style={{ backgroundColor: T.paper2 }}>
        <div className="mx-auto max-w-6xl px-5 md:px-8">
          {posts.length === 0 ? (
            <p style={{ color: T.inkSoft, textAlign: "center", padding: "48px 0" }}>No posts in this category yet.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {posts.map((post) => (
                <Link
                  key={post.slug}
                  to={`/blog/${post.slug}`}
                  className="rounded-2xl p-6 flex flex-col transition-shadow hover:shadow-md"
                  style={{
                    backgroundColor: "#fff",
                    border: `1px solid ${T.rule}`,
                    textDecoration: "none",
                    color: "inherit",
                  }}
                >
                  {/* Category + date */}
                  <div className="flex items-center gap-3 mb-3">
                    <span
                      className="text-[11px] font-semibold px-2.5 py-1 rounded"
                      style={{ backgroundColor: "rgba(255,91,31,0.08)", color: T.accent }}
                    >
                      {post.category}
                    </span>
                    <span className="text-[11px] font-mono" style={{ color: T.inkSoft }}>
                      {new Date(post.date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                    </span>
                  </div>

                  <h2
                    className="text-[16px] font-bold leading-snug mb-3 flex-1"
                    style={{ fontFamily: '"Bricolage Grotesque", "Inter", system-ui', letterSpacing: "-0.01em" }}
                  >
                    {post.title}
                  </h2>

                  <p className="text-[13px] leading-relaxed mb-4" style={{ color: T.inkSoft }}>
                    {post.excerpt}
                  </p>

                  <div className="flex items-center justify-between">
                    <span className="text-[11px] font-mono" style={{ color: T.inkSoft }}>{post.readTime}</span>
                    <span className="text-[13px] font-medium flex items-center gap-1" style={{ color: T.accent }}>
                      Read <ArrowRight className="w-3.5 h-3.5" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-16 md:py-20 text-center" style={{ backgroundColor: T.paper }}>
        <div className="mx-auto max-w-xl px-5">
          <h2
            className="text-xl md:text-2xl font-bold mb-3"
            style={{ fontFamily: '"Bricolage Grotesque", "Inter", system-ui', letterSpacing: "-0.02em" }}
          >
            Get creator tips in your inbox
          </h2>
          <p className="text-[14px] mb-6" style={{ color: T.inkSoft }}>
            One email per week. Niche ideas, growth strategies, and product updates.
          </p>
          {subscribed ? (
            <p style={{ color: T.accent, fontWeight: 600, fontSize: 15 }}>✓ You're subscribed!</p>
          ) : (
            <div className="flex flex-col sm:flex-row gap-3 justify-center max-w-md mx-auto">
              <input
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSubscribe()}
                className="text-[14px] px-4 py-3 rounded-xl flex-1"
                style={{ border: `1px solid ${T.rule}`, outline: "none", fontFamily: "inherit" }}
              />
              <button
                onClick={handleSubscribe}
                className="text-[14px] font-semibold px-6 py-3 rounded-xl transition-opacity hover:opacity-90"
                style={{ backgroundColor: T.accent, color: "#fff", border: 0, cursor: "pointer", fontFamily: "inherit" }}
              >
                Subscribe
              </button>
            </div>
          )}
        </div>
      </section>
    </>
  );
}

